/**
 * Bonus Popup for Poker Reborn
 *
 * Daily Bonus popup showing 7-day streak diagram, claim button,
 * and countdown timer. Players must claim each day to keep their
 * streak — miss a day and it resets!
 *
 * Follows the InboxPopup pattern:
 * - Extends Container directly (no base Popup class)
 * - Creates overlay, background panel, title, close button
 * - Subscribes to DailyBonusService events, cleans up in destroy()
 *
 * @see tasks/active/T37_DAILY_BONUS_PLAN.md - Feature design, wireframes
 * @see docs/screens/lobby/popups/BONUS_POPUP.md - Visual specs
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, GOLD_BUTTON_STYLE } from '@/components/common/Button';
import {
  dailyBonusService,
  type DailyBonusStatus,
  type DailyBonusClaimed,
  type ClaimedMilestone,
  type MonthlyProgress,
} from '@/services/DailyBonusService';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },
  popup: {
    width: 440,
    height: 580,
    widthMobile: 340,
    heightMobile: 520,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
    padding: 20,
    paddingMobile: 16,
  },
  title: {
    fill: 0xffd54f,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  closeButton: {
    size: 28,
    color: 0x888888,
  },
  instruction: {
    fill: 0xcccccc,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    wordWrap: true,
  },
  streakCell: {
    width: 50,
    height: 60,
    widthMobile: 38,
    heightMobile: 48,
    gap: 6,
    gapMobile: 4,
    cornerRadius: 8,
    claimed: {
      fill: 0x2e7d32,
      stroke: 0x4caf50,
    },
    current: {
      fill: 0x5d4037,
      stroke: 0xffd700,
    },
    future: {
      fill: 0x1a1a2e,
      stroke: 0x3a3a5e,
    },
  },
  dayLabel: {
    fill: 0xaaaaaa,
    fontSize: 11,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  rewardLabel: {
    fill: 0xffffff,
    fontSize: 13,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  statusIcon: {
    claimed: { fill: 0x4caf50, fontSize: 16 },
    current: { fill: 0xffd700, fontSize: 18 },
  },
  timerLabel: {
    fill: 0xff8a65,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  timerValue: {
    fill: 0xffffff,
    fontSize: 22,
    fontFamily: 'Roboto Mono, monospace',
    fontWeight: 'bold' as const,
  },
  stats: {
    fill: 0x888888,
    fontSize: 13,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  celebration: {
    fill: 0xffd700,
    fontSize: 32,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  loading: {
    fill: 0x888888,
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontStyle: 'italic' as const,
  },
  divider: {
    color: 0x3a3a5e,
    height: 1,
    marginY: 18,
  },
  milestone: {
    sectionGap: 8,
    header: {
      fill: 0xffd54f,
      fontSize: 16,
      fontFamily: 'Roboto, Arial, sans-serif',
      fontWeight: 'bold' as const,
    },
    explanation: {
      fill: 0x999999,
      fontSize: 12,
      fontFamily: 'Roboto, Arial, sans-serif',
    },
    progress: {
      fill: 0xcccccc,
      fontSize: 13,
      fontFamily: 'Roboto, Arial, sans-serif',
    },
    calendar: {
      cellWidth: 40,
      cellHeight: 28,
      cellWidthMobile: 32,
      cellHeightMobile: 24,
      cellGap: 3,
      cellGapMobile: 2,
      rowGap: 4,
      rowGapMobile: 3,
      cellRadius: 5,
      claimed: { fill: 0x2e7d32, stroke: 0x4caf50 },
      unclaimed: { fill: 0x1a1a2e, stroke: 0x3a3a5e },
      milestoneStroke: 0xffd700,
      checkmark: {
        fill: 0xffffff,
        fontSize: 14,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold' as const,
      },
      dayNumber: {
        fill: 0x555555,
        fontSize: 11,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
      rewardLabel: {
        fill: 0xcccccc,
        fontSize: 12,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold' as const,
      },
      rewardAchievedFill: 0x4caf50,
    },
  },
  milestoneCelebration: {
    label: {
      fill: 0xffffff,
      fontSize: 20,
      fontFamily: 'Roboto, Arial, sans-serif',
      fontWeight: 'bold' as const,
    },
    reward: {
      fill: 0xffd700,
      fontSize: 32,
      fontFamily: 'Roboto, Arial, sans-serif',
      fontWeight: 'bold' as const,
    },
    dailySmall: {
      fill: 0xffd700,
      fontSize: 24,
      fontFamily: 'Roboto, Arial, sans-serif',
      fontWeight: 'bold' as const,
    },
  },
  breakpoints: {
    mobile: 480,
  },
};

// =============================================================================
// BONUS POPUP
// =============================================================================

export class BonusPopup extends Container {
  private overlay: Graphics;
  private panel: Container;
  private popupWidth: number;
  private popupHeight: number;
  private padding: number;
  private screenWidth: number;
  private screenHeight: number;
  private isMobile: boolean;

  // Content containers
  private contentContainer: Container;
  private streakContainer: Container | null = null;
  private actionContainer: Container | null = null;
  private claimButton: Button | null = null;
  private timerContainer: Container | null = null;
  private timerValueText: Text | null = null;
  private instructionText: Text | null = null;
  private milestoneSection: Container | null = null;
  private statsText: Text | null = null;
  private loadingText: Text | null = null;
  private celebrationText: Text | null = null;
  private milestoneCelebrationContainer: Container | null = null;

  // State
  private currentStatus: DailyBonusStatus | null = null;
  private timerInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    screenWidth: number,
    screenHeight: number,
    initialStatus?: DailyBonusStatus,
  ) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    this.isMobile = screenWidth < STYLES.breakpoints.mobile;
    this.popupWidth = this.isMobile ? STYLES.popup.widthMobile : STYLES.popup.width;
    this.popupHeight = this.isMobile ? STYLES.popup.heightMobile : STYLES.popup.height;
    this.padding = this.isMobile ? STYLES.popup.paddingMobile : STYLES.popup.padding;

    // Full-screen semi-transparent overlay (tap to close)
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointertap', () => { this.close(); });
    this.addChild(this.overlay);

    // Centered panel container
    this.panel = new Container();
    this.panel.x = (screenWidth - this.popupWidth) / 2;
    this.panel.y = (screenHeight - this.popupHeight) / 2;
    this.addChild(this.panel);

    // Panel background
    const panelBackground = new Graphics();
    panelBackground.roundRect(0, 0, this.popupWidth, this.popupHeight, STYLES.popup.cornerRadius);
    panelBackground.fill(STYLES.popup.fill);
    panelBackground.setStrokeStyle({ width: 2, color: STYLES.popup.stroke });
    panelBackground.stroke();
    panelBackground.eventMode = 'static';
    panelBackground.on('pointerdown', (e) => { e.stopPropagation(); });
    this.panel.addChild(panelBackground);

    // Title
    const titleText = new Text({
      text: '\u2605 Daily Bonus',
      style: STYLES.title,
    });
    titleText.x = this.padding;
    titleText.y = this.padding;
    this.panel.addChild(titleText);

    // Close button (X)
    const closeBtn = this.createCloseButton();
    closeBtn.x = this.popupWidth - this.padding - STYLES.closeButton.size;
    closeBtn.y = this.padding;
    this.panel.addChild(closeBtn);

    // Content area (below title)
    this.contentContainer = new Container();
    this.contentContainer.y = this.padding + 36;
    this.panel.addChild(this.contentContainer);

    // Subscribe to service events
    dailyBonusService.on('statusUpdated', this.onStatusUpdated);
    dailyBonusService.on('claimed', this.onClaimed);
    dailyBonusService.on('error', this.onError);

    // Render initial state or show loading
    if (initialStatus) {
      this.currentStatus = initialStatus;
      this.renderBonusContent(initialStatus);
    } else {
      this.showLoading();
      dailyBonusService.requestStatus();
    }
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Resize overlay and re-center panel on window resize */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Resize overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Re-center panel
    this.panel.x = (width - this.popupWidth) / 2;
    this.panel.y = (height - this.popupHeight) / 2;
  }

  // ===========================================================================
  // LIFECYCLE
  // ===========================================================================

  private close(): void {
    this.destroy();
  }

  override destroy(): void {
    this.stopCountdown();
    dailyBonusService.off('statusUpdated', this.onStatusUpdated);
    dailyBonusService.off('claimed', this.onClaimed);
    dailyBonusService.off('error', this.onError);
    super.destroy({ children: true });
  }

  // ===========================================================================
  // SERVICE EVENT HANDLERS
  // ===========================================================================

  private onStatusUpdated = (status: DailyBonusStatus): void => {
    this.currentStatus = status;
    this.clearContent();
    this.renderBonusContent(status);
  };

  private onClaimed = (result: DailyBonusClaimed): void => {
    this.showCelebration(result.reward, result.milestone ?? undefined);

    // Longer celebration for milestone claims
    const celebrationDuration = result.milestone ? 2500 : 2000;

    // After celebration, update to cooldown view
    setTimeout(() => {
      if (this.destroyed) return;
      this.clearCelebration();

      if (this.currentStatus) {
        // Update monthly progress from cached status (already updated by service)
        const updatedMonthly = dailyBonusService.getLastStatus()?.monthly ?? this.currentStatus.monthly;

        const updatedStatus: DailyBonusStatus = {
          ...this.currentStatus,
          available: false,
          streakDay: result.streakDay,
          nextClaimAt: result.nextClaimAt,
          streakExpiresAt: result.streakExpiresAt,
          monthly: updatedMonthly,
        };
        this.currentStatus = updatedStatus;
        this.clearContent();
        this.renderBonusContent(updatedStatus);
      }
    }, celebrationDuration);
  };

  private onError = (_err: { code: string; message: string }): void => {
    // Re-enable claim button on error
    if (this.claimButton) {
      this.claimButton.disabled = false;
      const rewardText = this.currentStatus ? this.formatChips(this.currentStatus.nextReward) : '';
      this.claimButton.label = `CLAIM ${rewardText} CHIPS!`;
    }
  };

  // ===========================================================================
  // RENDERING
  // ===========================================================================

  private showLoading(): void {
    this.loadingText = new Text({
      text: 'Loading bonus...',
      style: STYLES.loading,
    });
    this.loadingText.anchor.set(0.5, 0.5);
    this.loadingText.x = this.popupWidth / 2;
    this.loadingText.y = (this.popupHeight - 60) / 2;
    this.contentContainer.addChild(this.loadingText);
  }

  private clearContent(): void {
    this.stopCountdown();
    this.contentContainer.removeChildren();
    this.streakContainer = null;
    this.actionContainer = null;
    this.claimButton = null;
    this.timerContainer = null;
    this.timerValueText = null;
    this.instructionText = null;
    this.milestoneSection = null;
    this.statsText = null;
    this.loadingText = null;
  }

  private renderBonusContent(status: DailyBonusStatus): void {
    const contentWidth = this.popupWidth - this.padding * 2;
    let currentY = 8;

    // Instruction text
    const instructionMessage = status.available
      ? 'Claim daily to build your streak and earn bigger rewards! Miss a day \u2014 streak resets!'
      : `Don't break the streak! Claim Day ${String(status.streakDay + 1)} before time runs out!`;

    this.instructionText = new Text({
      text: instructionMessage,
      style: {
        ...STYLES.instruction,
        wordWrapWidth: contentWidth,
      },
    });
    this.instructionText.x = this.padding;
    this.instructionText.y = currentY;
    this.contentContainer.addChild(this.instructionText);
    currentY += this.instructionText.height + 26;

    // Streak diagram
    this.streakContainer = this.buildStreakDiagram(status);
    // Center the diagram horizontally
    const diagramWidth = this.getStreakDiagramWidth();
    this.streakContainer.x = (this.popupWidth - diagramWidth) / 2;
    this.streakContainer.y = currentY;
    this.contentContainer.addChild(this.streakContainer);

    const cellHeight = this.isMobile ? STYLES.streakCell.heightMobile : STYLES.streakCell.height;
    currentY += cellHeight + 20;

    // Divider + Monthly milestones
    if (status.monthly.milestones.length > 0) {
      // Thin divider line
      currentY += STYLES.divider.marginY;
      const divider = new Graphics();
      divider.rect(this.padding, currentY, contentWidth, STYLES.divider.height);
      divider.fill(STYLES.divider.color);
      this.contentContainer.addChild(divider);
      currentY += STYLES.divider.height + STYLES.divider.marginY;

      this.milestoneSection = this.buildMilestoneSection(status.monthly);
      this.milestoneSection.x = this.padding;
      this.milestoneSection.y = currentY;
      this.contentContainer.addChild(this.milestoneSection);
      currentY += this.getMilestoneSectionHeight() + 12;
    }

    // Divider2 before action area
    currentY += STYLES.divider.marginY;
    const divider2 = new Graphics();
    divider2.rect(this.padding, currentY, contentWidth, STYLES.divider.height);
    divider2.fill(STYLES.divider.color);
    this.contentContainer.addChild(divider2);
    currentY += STYLES.divider.height + STYLES.divider.marginY;

    // Action area (claim button OR countdown timer)
    this.actionContainer = new Container();
    this.actionContainer.y = currentY;
    this.contentContainer.addChild(this.actionContainer);

    if (status.available) {
      // Claim button
      const buttonWidth = Math.min(contentWidth, 280);
      this.claimButton = new Button({
        label: `CLAIM ${this.formatChips(status.nextReward)} CHIPS!`,
        width: buttonWidth,
        height: 48,
        style: {
          ...GOLD_BUTTON_STYLE,
          fontSize: 16,
        },
      });
      this.claimButton.x = (this.popupWidth - buttonWidth) / 2;
      this.claimButton.onClick = () => { this.handleClaim(); };
      this.actionContainer.addChild(this.claimButton);
      currentY += 48 + 16;
    } else {
      // Timer
      this.timerContainer = new Container();
      this.actionContainer.addChild(this.timerContainer);

      const timerLabel = new Text({
        text: 'Claim before streak resets:',
        style: STYLES.timerLabel,
      });
      timerLabel.anchor.set(0.5, 0);
      timerLabel.x = this.popupWidth / 2;
      timerLabel.y = 0;
      this.timerContainer.addChild(timerLabel);

      this.timerValueText = new Text({
        text: '...',
        style: STYLES.timerValue,
      });
      this.timerValueText.anchor.set(0.5, 0);
      this.timerValueText.x = this.popupWidth / 2;
      this.timerValueText.y = 22;
      this.timerContainer.addChild(this.timerValueText);

      if (status.nextClaimAt) {
        this.startCountdown(status.nextClaimAt);
      }
      currentY += 56;
    }

    // Stats line
    const totalChipsFormatted = status.totalChipsEarned.toLocaleString();
    const streakLabel = status.streakDay > 0
      ? `Streak: Day ${String(status.streakDay)}`
      : 'No streak yet';

    this.statsText = new Text({
      text: `${streakLabel}  \u00B7  Total earned: ${totalChipsFormatted}`,
      style: STYLES.stats,
    });
    this.statsText.anchor.set(0.5, 0);
    this.statsText.x = this.popupWidth / 2;
    this.statsText.y = currentY;
    this.contentContainer.addChild(this.statsText);
  }

  // ===========================================================================
  // STREAK DIAGRAM
  // ===========================================================================

  private buildStreakDiagram(status: DailyBonusStatus): Container {
    const container = new Container();
    const cellWidth = this.isMobile ? STYLES.streakCell.widthMobile : STYLES.streakCell.width;
    const cellHeight = this.isMobile ? STYLES.streakCell.heightMobile : STYLES.streakCell.height;
    const cellGap = this.isMobile ? STYLES.streakCell.gapMobile : STYLES.streakCell.gap;

    const dayCount = Math.min(status.rewards.length, 7);

    for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
      const dayNumber = dayIndex + 1;
      const reward = status.rewards[dayIndex] ?? 0;

      // Determine cell state
      let cellState: 'claimed' | 'current' | 'future';
      if (status.available) {
        if (dayIndex < status.streakDay) cellState = 'claimed';
        else if (dayIndex === status.streakDay) cellState = 'current';
        else cellState = 'future';
      } else {
        if (dayIndex < status.streakDay) cellState = 'claimed';
        else cellState = 'future';
      }

      const cellX = dayIndex * (cellWidth + cellGap);
      const cell = this.renderDayCell(dayNumber, reward, cellState, cellWidth, cellHeight);
      cell.x = cellX;
      container.addChild(cell);
    }

    return container;
  }

  private renderDayCell(
    dayNumber: number,
    reward: number,
    state: 'claimed' | 'current' | 'future',
    cellWidth: number,
    cellHeight: number,
  ): Container {
    const cell = new Container();

    // Cell background
    const cellStyles = STYLES.streakCell[state];
    const background = new Graphics();
    background.roundRect(0, 0, cellWidth, cellHeight, STYLES.streakCell.cornerRadius);
    background.fill(cellStyles.fill);
    background.setStrokeStyle({ width: state === 'current' ? 2 : 1, color: cellStyles.stroke });
    background.stroke();

    // Add glow for current day
    if (state === 'current') {
      const glow = new Graphics();
      glow.roundRect(-2, -2, cellWidth + 4, cellHeight + 4, STYLES.streakCell.cornerRadius + 2);
      glow.fill({ color: 0xffd700, alpha: 0.15 });
      cell.addChild(glow);
    }

    cell.addChild(background);

    // Day label ("D1", "D2", ...)
    const dayLabel = new Text({
      text: `D${String(dayNumber)}`,
      style: {
        ...STYLES.dayLabel,
        fill: state === 'current' ? 0xffd700 : STYLES.dayLabel.fill,
      },
    });
    dayLabel.anchor.set(0.5, 0);
    dayLabel.x = cellWidth / 2;
    dayLabel.y = 4;
    cell.addChild(dayLabel);

    // Reward amount
    const rewardLabel = new Text({
      text: this.formatChips(reward),
      style: {
        ...STYLES.rewardLabel,
        fontSize: this.isMobile ? 10 : 12,
        fill: state === 'future' ? 0x666666 : 0xffffff,
      },
    });
    rewardLabel.anchor.set(0.5, 0.5);
    rewardLabel.x = cellWidth / 2;
    rewardLabel.y = cellHeight / 2;
    cell.addChild(rewardLabel);

    // Status icon
    if (state === 'claimed') {
      const checkmark = new Text({
        text: '\u2713',
        style: {
          fill: STYLES.statusIcon.claimed.fill,
          fontSize: STYLES.statusIcon.claimed.fontSize,
          fontFamily: 'Roboto, Arial, sans-serif',
        },
      });
      checkmark.anchor.set(0.5, 1);
      checkmark.x = cellWidth / 2;
      checkmark.y = cellHeight - 3;
      cell.addChild(checkmark);
    } else if (state === 'current') {
      const star = new Text({
        text: '\u2605',
        style: {
          fill: STYLES.statusIcon.current.fill,
          fontSize: STYLES.statusIcon.current.fontSize,
          fontFamily: 'Roboto, Arial, sans-serif',
        },
      });
      star.anchor.set(0.5, 1);
      star.x = cellWidth / 2;
      star.y = cellHeight - 2;
      cell.addChild(star);
    }

    return cell;
  }

  private getStreakDiagramWidth(): number {
    const cellWidth = this.isMobile ? STYLES.streakCell.widthMobile : STYLES.streakCell.width;
    const cellGap = this.isMobile ? STYLES.streakCell.gapMobile : STYLES.streakCell.gap;
    return 7 * cellWidth + 6 * cellGap;
  }

  // ===========================================================================
  // MONTHLY MILESTONES
  // ===========================================================================

  private buildMilestoneSection(monthly: MonthlyProgress): Container {
    const section = new Container();
    const contentWidth = this.popupWidth - this.padding * 2;
    const cal = STYLES.milestone.calendar;
    const cellW = this.isMobile ? cal.cellWidthMobile : cal.cellWidth;
    const cellH = this.isMobile ? cal.cellHeightMobile : cal.cellHeight;
    const cellGap = this.isMobile ? cal.cellGapMobile : cal.cellGap;
    const rowGap = this.isMobile ? cal.rowGapMobile : cal.rowGap;
    let currentY = 0;

    // Header: "Monthly Bonuses"
    const headerText = new Text({
      text: 'Monthly Bonuses',
      style: STYLES.milestone.header,
    });
    headerText.anchor.set(0.5, 0);
    headerText.x = contentWidth / 2;
    headerText.y = currentY;
    section.addChild(headerText);
    currentY += headerText.height + 6;

    // Explanation: how it works
    const explanationText = new Text({
      text: 'Claim on different days each month for bonus rewards!',
      style: {
        ...STYLES.milestone.explanation,
        wordWrap: true,
        wordWrapWidth: contentWidth,
      },
    });
    explanationText.anchor.set(0.5, 0);
    explanationText.x = contentWidth / 2;
    explanationText.y = currentY;
    section.addChild(explanationText);
    currentY += explanationText.height + 14;

    // Calendar grid: 4 rows x 7 columns = 28 day slots
    const totalCols = 7;
    const totalRows = 4;
    const gridWidth = totalCols * cellW + (totalCols - 1) * cellGap;
    const rewardGap = this.isMobile ? 6 : 10;
    const rewardLabelWidth = this.isMobile ? 36 : 44;
    const totalRowWidth = gridWidth + rewardGap + rewardLabelWidth;
    const gridStartX = (contentWidth - totalRowWidth) / 2;

    // Set of milestone day numbers for gold-border highlighting
    const milestoneDays = new Set(monthly.milestones.map((m) => m.days));

    for (let row = 0; row < totalRows; row++) {
      const rowY = currentY + row * (cellH + rowGap);

      for (let col = 0; col < totalCols; col++) {
        const dayNum = row * totalCols + col + 1;
        const isClaimed = dayNum <= monthly.claimCount;
        const isMilestoneDay = milestoneDays.has(dayNum);

        const cellX = gridStartX + col * (cellW + cellGap);
        const cell = this.renderCalendarCell(
          dayNum, isClaimed, isMilestoneDay, cellW, cellH, cal.cellRadius,
        );
        cell.x = cellX;
        cell.y = rowY;
        section.addChild(cell);
      }

      // Milestone reward label at end of row
      const rowMilestone = monthly.milestones[row];
      if (rowMilestone) {
        const labelX = gridStartX + gridWidth + rewardGap;
        const labelY = rowY + cellH / 2;

        const rewardLabelText = rowMilestone.achieved
          ? `${this.formatChips(rowMilestone.reward)} \u2713`
          : this.formatChips(rowMilestone.reward);

        const rewardLabel = new Text({
          text: rewardLabelText,
          style: {
            ...cal.rewardLabel,
            fill: rowMilestone.achieved ? cal.rewardAchievedFill : cal.rewardLabel.fill,
          },
        });
        rewardLabel.anchor.set(0, 0.5);
        rewardLabel.x = labelX;
        rewardLabel.y = labelY;
        section.addChild(rewardLabel);
      }
    }

    currentY += totalRows * cellH + (totalRows - 1) * rowGap + 12;

    // Progress text: "15 / 28 days this month"
    const lastMilestone = monthly.milestones[monthly.milestones.length - 1];
    const maxDays = lastMilestone?.days ?? 28;
    const progressText = new Text({
      text: `${String(monthly.claimCount)} / ${String(maxDays)} days this month`,
      style: STYLES.milestone.progress,
    });
    progressText.anchor.set(0.5, 0);
    progressText.x = contentWidth / 2;
    progressText.y = currentY;
    section.addChild(progressText);

    return section;
  }

  private renderCalendarCell(
    dayNumber: number,
    isClaimed: boolean,
    isMilestoneDay: boolean,
    cellWidth: number,
    cellHeight: number,
    cellRadius: number,
  ): Container {
    const cell = new Container();
    const cal = STYLES.milestone.calendar;

    const bgColor = isClaimed ? cal.claimed.fill : cal.unclaimed.fill;
    const strokeColor = isMilestoneDay
      ? cal.milestoneStroke
      : isClaimed ? cal.claimed.stroke : cal.unclaimed.stroke;
    const strokeWidth = isMilestoneDay ? 1.5 : 1;

    const background = new Graphics();
    background.roundRect(0, 0, cellWidth, cellHeight, cellRadius);
    background.fill(bgColor);
    background.setStrokeStyle({ width: strokeWidth, color: strokeColor });
    background.stroke();
    cell.addChild(background);

    if (isClaimed) {
      // White checkmark
      const checkmark = new Text({
        text: '\u2713',
        style: cal.checkmark,
      });
      checkmark.anchor.set(0.5, 0.5);
      checkmark.x = cellWidth / 2;
      checkmark.y = cellHeight / 2;
      cell.addChild(checkmark);
    } else {
      // Day number (gold for milestone days, dim gray for regular)
      const dayLabel = new Text({
        text: String(dayNumber),
        style: {
          ...cal.dayNumber,
          fill: isMilestoneDay ? cal.milestoneStroke : cal.dayNumber.fill,
        },
      });
      dayLabel.anchor.set(0.5, 0.5);
      dayLabel.x = cellWidth / 2;
      dayLabel.y = cellHeight / 2;
      cell.addChild(dayLabel);
    }

    return cell;
  }

  private getMilestoneSectionHeight(): number {
    const cellH = this.isMobile
      ? STYLES.milestone.calendar.cellHeightMobile
      : STYLES.milestone.calendar.cellHeight;
    const rowGap = this.isMobile
      ? STYLES.milestone.calendar.rowGapMobile
      : STYLES.milestone.calendar.rowGap;
    // header(~18) + gap(6) + explanation(~15) + gap(14) + grid(4 rows) + gap(12) + progress(~15)
    return 18 + 6 + 15 + 14 + (cellH * 4 + rowGap * 3) + 12 + 15;
  }

  // ===========================================================================
  // CLAIM HANDLING
  // ===========================================================================

  private handleClaim(): void {
    if (this.claimButton) {
      this.claimButton.disabled = true;
      this.claimButton.label = 'Claiming...';
    }
    dailyBonusService.claim();
  }

  // ===========================================================================
  // CELEBRATION
  // ===========================================================================

  private showCelebration(reward: number, milestone?: ClaimedMilestone): void {
    if (milestone) {
      this.showMilestoneCelebration(reward, milestone);
    } else {
      this.showSimpleCelebration(reward);
    }
  }

  private showSimpleCelebration(reward: number): void {
    this.celebrationText = new Text({
      text: `+${this.formatChips(reward)} chips!`,
      style: STYLES.celebration,
    });
    this.celebrationText.anchor.set(0.5, 0.5);
    this.celebrationText.x = this.popupWidth / 2;
    this.celebrationText.y = this.popupHeight / 2 - 30;
    this.celebrationText.alpha = 0;
    this.panel.addChild(this.celebrationText);

    this.animateFadeScale(this.celebrationText);
  }

  private showMilestoneCelebration(totalReward: number, milestone: ClaimedMilestone): void {
    const dailyReward = totalReward - milestone.reward;

    this.milestoneCelebrationContainer = new Container();
    this.milestoneCelebrationContainer.x = this.popupWidth / 2;
    this.milestoneCelebrationContainer.y = this.popupHeight / 2 - 60;
    this.panel.addChild(this.milestoneCelebrationContainer);

    // Daily reward line (smaller)
    const dailyText = new Text({
      text: `+${this.formatChips(dailyReward)} chips!`,
      style: STYLES.milestoneCelebration.dailySmall,
    });
    dailyText.anchor.set(0.5, 0.5);
    dailyText.y = 0;
    dailyText.alpha = 0;
    this.milestoneCelebrationContainer.addChild(dailyText);

    this.animateFadeScale(dailyText);

    // Milestone label + reward (delayed)
    setTimeout(() => {
      if (this.destroyed || !this.milestoneCelebrationContainer) return;

      const milestoneLabel = new Text({
        text: `${String(milestone.days)}-Day Milestone!`,
        style: STYLES.milestoneCelebration.label,
      });
      milestoneLabel.anchor.set(0.5, 0.5);
      milestoneLabel.y = 40;
      milestoneLabel.alpha = 0;
      this.milestoneCelebrationContainer.addChild(milestoneLabel);

      const milestoneRewardText = new Text({
        text: `+${this.formatChips(milestone.reward)} chips!`,
        style: STYLES.milestoneCelebration.reward,
      });
      milestoneRewardText.anchor.set(0.5, 0.5);
      milestoneRewardText.y = 72;
      milestoneRewardText.alpha = 0;
      this.milestoneCelebrationContainer.addChild(milestoneRewardText);

      this.animateFadeScale(milestoneLabel);
      this.animateFadeScale(milestoneRewardText);
    }, 500);
  }

  private animateFadeScale(target: Text): void {
    let animationFrame = 0;
    const totalFrames = 30;
    const animate = (): void => {
      if (this.destroyed || target.destroyed) return;
      animationFrame++;
      const progress = Math.min(animationFrame / totalFrames, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      target.alpha = eased;
      target.scale.set(0.5 + 0.5 * eased);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }

  private clearCelebration(): void {
    if (this.celebrationText) {
      this.celebrationText.destroy();
      this.celebrationText = null;
    }
    if (this.milestoneCelebrationContainer) {
      this.milestoneCelebrationContainer.destroy({ children: true });
      this.milestoneCelebrationContainer = null;
    }
  }

  // ===========================================================================
  // COUNTDOWN TIMER
  // ===========================================================================

  private startCountdown(nextClaimAt: string): void {
    this.updateTimerDisplay(nextClaimAt);
    this.timerInterval = setInterval(() => {
      const remaining = new Date(nextClaimAt).getTime() - Date.now();
      if (remaining <= 0) {
        this.stopCountdown();
        // Timer hit zero — refresh status (bonus should be available now)
        dailyBonusService.requestStatus();
        return;
      }
      this.updateTimerDisplay(nextClaimAt);
    }, 1000);
  }

  private updateTimerDisplay(nextClaimAt: string): void {
    const remaining = Math.max(0, new Date(nextClaimAt).getTime() - Date.now());
    const hours = Math.floor(remaining / 3_600_000);
    const minutes = Math.floor((remaining % 3_600_000) / 60_000);
    const seconds = Math.floor((remaining % 60_000) / 1_000);

    if (this.timerValueText) {
      this.timerValueText.text = `${String(hours)}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    }
  }

  private stopCountdown(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  // ===========================================================================
  // UI HELPERS
  // ===========================================================================

  private formatChips(amount: number): string {
    if (amount >= 1000) {
      const thousands = amount / 1000;
      return thousands % 1 === 0 ? `${String(thousands)}K` : `${thousands.toFixed(1)}K`;
    }
    return String(amount);
  }

  private createCloseButton(): Container {
    const container = new Container();
    const size = STYLES.closeButton.size;

    const background = new Graphics();
    background.roundRect(0, 0, size, size, 4);
    background.fill({ color: 0x000000, alpha: 0 });
    container.addChild(background);

    // Draw X
    const xGraphics = new Graphics();
    xGraphics.setStrokeStyle({ width: 2, color: STYLES.closeButton.color });
    xGraphics.moveTo(6, 6);
    xGraphics.lineTo(size - 6, size - 6);
    xGraphics.moveTo(size - 6, 6);
    xGraphics.lineTo(6, size - 6);
    xGraphics.stroke();
    container.addChild(xGraphics);

    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', () => { this.close(); });

    return container;
  }
}
